/**
 * Clarity Card — the one-page share asset.
 *
 * It carries exactly two things from the Passport: the cover (who this is
 * for, when, and the seal) and the identity page (the archetype pair, the
 * sentence, the cause, the three signals said in words).
 *
 * What it deliberately never carries: a score, a percentage, a bar, a pay
 * band, a ranked job title. A card gets screenshotted into a group chat, and
 * a number out of context is exactly the thing this product refuses to do.
 *
 * Rendered at a fixed 1080×1350 (Instagram portrait) with literal colours
 * rather than CSS variables, because `html-to-image` rasterises a detached
 * clone and a themed variable can resolve to nothing there.
 */
import { forwardRef } from "react";
import type { Dossier } from "../dossier";
import { REPORT_NAME } from "../dossier";

export type ClarityCardTheme = "glass" | "dark" | "light";

export const CLARITY_THEMES: Array<{
  key: ClarityCardTheme; label: string; hint: string; swatch: string;
}> = [
  { key: "glass", label: "Signature", hint: "Brand green into zaffre", swatch: "linear-gradient(135deg,#00665B,#0818A8)" },
  { key: "dark", label: "Midnight", hint: "Matches your dashboard", swatch: "#0B0F10" },
  { key: "light", label: "Paper", hint: "Prints and photocopies well", swatch: "#F4F3EF" },
];

interface Skin {
  bg: string;
  ink: string;
  soft: string;
  accent: string;
  chipBg: string;
  chipInk: string;
  hair: string;
}

const SKINS: Record<ClarityCardTheme, Skin> = {
  glass: {
    bg: "linear-gradient(150deg,#00665B 0%,#04463F 42%,#0818A8 100%)",
    ink: "#FFFFFF",
    soft: "rgba(255,255,255,0.74)",
    accent: "#FF5F1F",
    chipBg: "rgba(255,255,255,0.14)",
    chipInk: "#FFFFFF",
    hair: "rgba(255,255,255,0.22)",
  },
  dark: {
    bg: "linear-gradient(160deg,#0B0F10 0%,#111917 60%,#0B0F10 100%)",
    ink: "#F7F7F5",
    soft: "rgba(247,247,245,0.66)",
    accent: "#2FD4A8",
    chipBg: "rgba(47,212,168,0.12)",
    chipInk: "#8FEBD2",
    hair: "rgba(247,247,245,0.16)",
  },
  light: {
    bg: "linear-gradient(160deg,#F7F6F2 0%,#EAE7DF 100%)",
    ink: "#180205",
    soft: "rgba(24,2,5,0.62)",
    accent: "#00665B",
    chipBg: "rgba(0,102,91,0.10)",
    chipInk: "#00665B",
    hair: "rgba(24,2,5,0.14)",
  },
};

export interface ClarityCardProps {
  dossier: Dossier;
  theme: ClarityCardTheme;
  /** Referral handle, when the learner has one. Printed as a share link. */
  handle?: string | null;
}

/**
 * A signal, said as words only. `IdentitySignal.label` is already
 * score-free ("Builder first", "Fits Healthcare"); the value is dropped here
 * on purpose and never re-derived.
 */
function SignalChip({ text, skin }: { text: string; skin: Skin }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "14px 26px",
        borderRadius: 999,
        background: skin.chipBg,
        color: skin.chipInk,
        fontSize: 30,
        fontWeight: 600,
        lineHeight: 1.1,
        border: `1px solid ${skin.hair}`,
      }}
    >
      {text}
    </span>
  );
}

const ClarityCard = forwardRef<HTMLDivElement, ClarityCardProps>(
  function ClarityCard({ dossier: d, theme, handle }, ref) {
    const skin = SKINS[theme];
    const i = d.identity;
    const shareUrl = handle ? `wekit.ai/ref/${handle}` : "wekit.ai";

    return (
      <div
        ref={ref}
        style={{
          width: 1080,
          height: 1350,
          background: skin.bg,
          color: skin.ink,
          padding: 84,
          display: "flex",
          flexDirection: "column",
          fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {/* --- cover band ------------------------------------------------ */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 44, fontWeight: 700, letterSpacing: "-0.02em" }}>WeKIT</div>
            <div style={{ fontSize: 24, color: skin.soft, marginTop: 6 }}>
              Career Clarity 360™
            </div>
          </div>
          <div style={{ textAlign: "right", maxWidth: 380 }}>
            <div
              style={{
                fontSize: 20,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                color: skin.accent,
                fontWeight: 600,
              }}
            >
              Passport
            </div>
            <div style={{ fontSize: 22, color: skin.soft, marginTop: 8 }}>{d.bandLabel}</div>
          </div>
        </div>

        <div style={{ height: 1, background: skin.hair, margin: "44px 0 0" }} />

        {/* --- identity -------------------------------------------------- */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: 26, color: skin.soft, letterSpacing: "0.06em" }}>
            {d.learnerName}
          </div>
          <div
            style={{
              fontSize: i.pairLabel.length > 34 ? 84 : 98,
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.02,
              marginTop: 18,
            }}
          >
            {i.pairLabel}
          </div>
          <div style={{ fontSize: 36, lineHeight: 1.35, color: skin.ink, marginTop: 32, maxWidth: 860 }}>
            {i.sentence}
          </div>
          {i.causeLine && (
            <div style={{ fontSize: 30, lineHeight: 1.35, color: skin.accent, marginTop: 26, fontWeight: 600 }}>
              {i.causeLine}
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 44 }}>
            {i.signals.map((s) => (
              <SignalChip key={s.label} text={s.label} skin={skin} />
            ))}
          </div>
        </div>

        {/* --- foot ------------------------------------------------------- */}
        <div style={{ height: 1, background: skin.hair, marginBottom: 32 }} />
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>Your {REPORT_NAME}</div>
            <div style={{ fontSize: 20, color: skin.soft, marginTop: 8 }}>
              {d.generatedOn} · no scores on this card, on purpose
            </div>
          </div>
          <div style={{ textAlign: "right", fontSize: 20, color: skin.soft }}>
            <div>{shareUrl}</div>
            <div style={{ marginTop: 6, fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
              {d.reportHash ? `Seal ${d.reportHash.slice(0, 10)}` : `Attempt ${d.attemptRef}`}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

export default ClarityCard;
